// The case proforma and the per-arch production counters.
//
// A case is planned as an upper model, a lower model, or both, each with its own number of
// aligners. That pair is what the doctor approves (with the price), what production counts against
// at Printing / Punching / Cutting, and what is printed on the slip packed with the aligners. So the
// inputs, the counters and the print view all live here and read the same two numbers.
import { useEffect, useState } from 'react';
import { WF_STAGE_ROLES as R, WF_ARCH_LABELS, WF_PRIORITY_LABELS, formatDate, type WfCase, type WfCaseProforma } from '@alignova/shared';
import { useCan } from '../../auth/useCan.ts';
import { wfApi } from './workflowApi.ts';
import { Btn, inputCls } from './wfUi.tsx';

/** Stages where the upper/lower counters are the work in hand. */
export const PRODUCTION_COUNT_STAGES: readonly string[] = [R.printing, R.punching, R.cutting, R.packaging];

/* -- upper / lower aligner numbers ------------------------------------------------------------- */

export function ArchTrayInputs({
  upper,
  lower,
  onChange,
  autoFocus,
}: {
  upper: string;
  lower: string;
  onChange: (upper: string, lower: string) => void;
  autoFocus?: boolean;
}) {
  const total = (Number(upper) || 0) + (Number(lower) || 0);
  const field = (label: string, v: string, set: (x: string) => void, focus?: boolean) => (
    <label className="flex items-center gap-1.5 text-sm">
      <span className="w-6 rounded bg-surface-2 py-0.5 text-center text-xs font-bold">{label}</span>
      <input
        type="number"
        min="0"
        max="200"
        inputMode="numeric"
        className={`${inputCls} w-24`}
        placeholder="0"
        value={v}
        autoFocus={focus}
        onChange={(e) => set(e.target.value)}
      />
    </label>
  );
  return (
    <div className="flex flex-wrap items-center gap-3">
      {field('U', upper, (x) => onChange(x, lower), autoFocus)}
      {field('L', lower, (x) => onChange(upper, x))}
      <span className="text-xs text-muted">
        {archOfCounts(Number(upper) || 0, Number(lower) || 0)} · <b className="tabnum text-ink">{total}</b> aligners
      </span>
    </div>
  );
}

export function archOfCounts(upper: number, lower: number): string {
  if (upper > 0 && lower > 0) return WF_ARCH_LABELS.both;
  if (upper > 0) return WF_ARCH_LABELS.upper;
  if (lower > 0) return WF_ARCH_LABELS.lower;
  return 'No models yet';
}

/** "U 12 · L 10 · 22 aligners" for a case, or null when nothing is planned. */
export function alignerSummary(c: Pick<WfCase, 'trays' | 'upperTrays' | 'lowerTrays' | 'arch'>): string | null {
  const u = c.upperTrays ?? 0;
  const l = c.lowerTrays ?? 0;
  const total = c.trays ?? u + l;
  if (!total) return null;
  if (!u && !l) return `${total} aligners${c.arch ? ` · ${WF_ARCH_LABELS[c.arch]}` : ''}`;
  return `U ${u} · L ${l} · ${total} aligners`;
}

/* -- production counters ----------------------------------------------------------------------- */

type CounterKey = 'printedUpper' | 'printedLower' | 'punchedUpper' | 'punchedLower' | 'cutUpper' | 'cutLower';
const ROWS: Array<{ label: string; stage: string; upper: CounterKey; lower: CounterKey }> = [
  { label: 'Printed', stage: R.printing, upper: 'printedUpper', lower: 'printedLower' },
  { label: 'Punched', stage: R.punching, upper: 'punchedUpper', lower: 'punchedLower' },
  { label: 'Cut', stage: R.cutting, upper: 'cutUpper', lower: 'cutLower' },
];

/**
 * Upper / lower done-versus-planned for printing, punching and cutting, editable in place. The row
 * for the case's current stage is highlighted; the others stay visible so a Punching operator can
 * see how many trays Printing actually delivered.
 */
export function ProductionCounters({ c, onChanged, compact }: { c: WfCase; onChanged?: () => void; compact?: boolean }) {
  const can = useCan();
  const editable = can('workflow:production') || can('workflow:update');
  const [vals, setVals] = useState<Record<CounterKey, number>>(pick(c));
  const [busy, setBusy] = useState<CounterKey | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Re-sync when the server sends fresh counts (another operator, the +1 button, a live event).
  useEffect(() => setVals(pick(c)), [c]);

  const planned = plannedArches(c);
  const known = planned.known;

  const save = async (k: CounterKey, n: number) => {
    const v = Math.max(0, Math.min(500, Math.round(n)));
    setVals((p) => ({ ...p, [k]: v }));
    setBusy(k);
    setErr(null);
    try {
      await wfApi.patch(c.id, { [k]: v });
      onChanged?.();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(null);
    }
  };

  const cell = (k: CounterKey, target: number) => {
    const v = vals[k];
    const left = Math.max(0, target - v);
    const done = target > 0 && v >= target;
    return (
      <td className="py-1 pr-2 align-middle">
        <div className="flex items-center gap-1">
          {editable ? (
            <input
              type="number"
              min="0"
              max="500"
              inputMode="numeric"
              className={`${inputCls} w-16 px-1.5 py-0.5 text-center tabnum`}
              value={v}
              disabled={busy === k}
              onChange={(e) => setVals((p) => ({ ...p, [k]: Number(e.target.value) || 0 }))}
              onBlur={(e) => { const n = Number(e.target.value) || 0; if (n !== c[k]) void save(k, n); }}
            />
          ) : (
            <b className="tabnum">{v}</b>
          )}
          <span className="tabnum text-xs text-muted">/ {known ? target : '?'}</span>
          {editable && known && left > 0 && (
            <button type="button" className="rounded border border-line px-1.5 text-xs hover:bg-surface-2" disabled={busy === k} onClick={() => void save(k, v + 1)} title="One more">+1</button>
          )}
          {known && (done ? <span className="text-xs text-status-progress-fg">✓</span> : left > 0 ? <span className="text-xs text-status-warn-fg">{left} left</span> : null)}
        </div>
      </td>
    );
  };

  return (
    <div className={compact ? '' : 'rounded-lg border border-line bg-surface p-4 shadow-level-1'}>
      <div className="flex flex-wrap items-baseline gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted">Aligners in production</div>
        <div className="text-xs text-muted">{alignerSummary(c) ?? 'Upper / lower aligner numbers not set yet - set them in Overview'}</div>
      </div>
      <table className="mt-2 text-sm">
        <thead className="text-left text-[11px] uppercase tracking-wider text-muted">
          <tr><th className="py-1 pr-4">Stage</th><th className="pr-2">Upper</th><th className="pr-2">Lower</th></tr>
        </thead>
        <tbody>
          {ROWS.map((r) => (
            <tr key={r.label} className={`border-t border-line ${c.stageKey === r.stage ? 'bg-accent/10 font-medium' : ''}`}>
              <td className="py-1 pr-4">{r.label}{c.stageKey === r.stage ? <span className="ml-1 text-[10px] uppercase text-accent">now</span> : null}</td>
              {cell(r.upper, planned.upper)}
              {cell(r.lower, planned.lower)}
            </tr>
          ))}
        </tbody>
      </table>
      {err && <div className="mt-1 text-xs text-status-urgent-fg">{err}</div>}
    </div>
  );
}

/** Planned upper / lower for a case (a single-arch case with only a total still has a target). */
export function plannedArches(c: Pick<WfCase, 'trays' | 'upperTrays' | 'lowerTrays' | 'arch'>): { upper: number; lower: number; known: boolean } {
  return {
    upper: c.upperTrays ?? (c.arch === 'upper' ? (c.trays ?? 0) : 0),
    lower: c.lowerTrays ?? (c.arch === 'lower' ? (c.trays ?? 0) : 0),
    known: c.upperTrays != null || c.lowerTrays != null || ((c.arch === 'upper' || c.arch === 'lower') && !!c.trays),
  };
}

/** "Printed · U 5/10 · L 5/10" for the case's current production stage, or null elsewhere. */
export function stageProgress(c: WfCase): { label: string; upper: [number, number]; lower: [number, number]; known: boolean; left: number } | null {
  const row = ROWS.find((r) => r.stage === c.stageKey);
  if (!row) return null;
  const plan = plannedArches(c);
  const u = c[row.upper];
  const l = c[row.lower];
  return { label: row.label, upper: [u, plan.upper], lower: [l, plan.lower], known: plan.known, left: Math.max(0, plan.upper - u) + Math.max(0, plan.lower - l) };
}

function pick(c: WfCase): Record<CounterKey, number> {
  return { printedUpper: c.printedUpper, printedLower: c.printedLower, punchedUpper: c.punchedUpper, punchedLower: c.punchedLower, cutUpper: c.cutUpper, cutLower: c.cutLower };
}

/* -- the proforma itself ----------------------------------------------------------------------- */

const pkr = (n: number | null | undefined) => (n == null ? '-' : `PKR ${n.toLocaleString('en-PK')}`);

/** The proforma as a small table, for the doctor's approval card and the lab's case page. */
export function ProformaTable({ p, showPrice = true }: { p: WfCaseProforma; showPrice?: boolean }) {
  const Row = ({ k, v, strong }: { k: string; v: React.ReactNode; strong?: boolean }) => (
    <tr className="border-t border-line/70">
      <td className="py-1 pr-3 text-muted">{k}</td>
      <td className={`py-1 tabnum ${strong ? 'font-semibold text-ink' : ''}`}>{v ?? '-'}</td>
    </tr>
  );
  return (
    <div>
      <div className="mb-2 grid grid-cols-2 gap-3 rounded-lg bg-surface-2 px-3 py-2">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Patient</div>
          <div className="text-lg font-bold leading-tight text-ink">{p.patientName}</div>
          {p.patientCode && <div className="font-mono text-xs text-muted">{p.patientCode}</div>}
        </div>
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted">Clinic</div>
          <div className="text-lg font-bold leading-tight text-ink">{p.clinicName ?? p.doctorName ?? '-'}</div>
          {p.clinicName && p.doctorName && <div className="text-xs text-muted">{p.doctorName}</div>}
        </div>
      </div>
    <table className="w-full text-sm">
      <tbody>
        <Row k="Case" v={<span className="font-mono">{p.caseNumber}</span>} />
        <Row k="Models" v={p.archLabel} />
        <Row k="Upper aligners" v={p.upperTrays ?? (p.arch === 'lower' ? 0 : null)} />
        <Row k="Lower aligners" v={p.lowerTrays ?? (p.arch === 'upper' ? 0 : null)} />
        <Row k="Total aligners" v={p.trays} strong />
        {showPrice && p.perTrayRupees != null && <Row k="Price per aligner" v={pkr(p.perTrayRupees)} />}
        {showPrice && p.grossRupees != null && <Row k="Aligners" v={pkr(p.grossRupees)} />}
        {showPrice && p.discountLabel && <Row k={`Discount (${p.discountLabel})`} v={`- ${pkr(p.discountRupees)}`} />}
        {showPrice && p.expressLabel && <Row k={p.expressLabel} v={pkr(p.expressRupees)} />}
        {showPrice && p.adjustmentRupees !== 0 && <Row k="Adjustment" v={`${p.adjustmentRupees > 0 ? '+' : '-'} ${pkr(Math.abs(p.adjustmentRupees))}`} />}
        {showPrice && p.totalRupees != null && <Row k="Total to clinic" v={pkr(p.totalRupees)} strong />}
        <Row k="Turnaround" v={`${WF_PRIORITY_LABELS[p.priority] ?? p.priority}${p.dueDate ? ` · due ${formatDate(p.dueDate)}` : ''}`} />
        {p.setupVersion > 0 && <Row k="Setup version" v={`V${p.setupVersion}`} />}
      </tbody>
    </table>
    </div>
  );
}

/** Opens the proforma in a print window - the slip that travels in the box with the aligners. */
export function ProformaPrintButton({ caseId, label = 'Print proforma', withPrice = false, tone = 'ghost' }: { caseId: string; label?: string; withPrice?: boolean; tone?: 'ghost' | 'accent' }) {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const go = async () => {
    setBusy(true);
    setErr(null);
    // Open the window synchronously with the click so pop-up blockers let it through, then fill it.
    const w = window.open('', '_blank');
    try {
      const p = await wfApi.proforma(caseId);
      const html = proformaHtml(p, withPrice);
      if (!w) throw new Error('The browser blocked the print window. Allow pop-ups for this site and try again.');
      w.document.open();
      w.document.write(html);
      w.document.close();
    } catch (e) {
      w?.close();
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <span className="inline-flex flex-col">
      <Btn tone={tone} disabled={busy} onClick={() => void go()}>{busy ? 'Preparing…' : `🖨 ${label}`}</Btn>
      {err && <span className="mt-1 text-xs text-status-urgent-fg">{err}</span>}
    </span>
  );
}

const esc = (s: string | number | null | undefined) => String(s ?? '').replace(/[&<>"']/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]!);

function proformaHtml(p: WfCaseProforma, withPrice: boolean): string {
  const row = (k: string, v: string | number | null | undefined, strong = false) =>
    `<tr><th>${esc(k)}</th><td${strong ? ' class="strong"' : ''}>${esc(v ?? '-')}</td></tr>`;
  const price = withPrice && p.perTrayRupees != null
    ? row('Price per aligner', pkr(p.perTrayRupees)) +
      row('Aligners', pkr(p.grossRupees)) +
      (p.discountLabel ? row(`Discount (${p.discountLabel})`, `- ${pkr(p.discountRupees)}`) : '') +
      (p.expressLabel ? row(p.expressLabel, pkr(p.expressRupees)) : '') +
      (p.adjustmentRupees ? row('Adjustment', `${p.adjustmentRupees > 0 ? '+' : '-'} ${pkr(Math.abs(p.adjustmentRupees))}`) : '') +
      row('Total to clinic', pkr(p.totalRupees), true)
    : '';
  return `<!doctype html><html><head><meta charset="utf-8"><title>Proforma ${esc(p.caseNumber)}</title>
<style>
  body{font-family:system-ui,Segoe UI,Arial,sans-serif;color:#0f172a;margin:0;padding:28px;font-size:14px}
  .head{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:2px solid #0f172a;padding-bottom:10px;margin-bottom:16px}
  .brand{font-size:20px;font-weight:800;letter-spacing:-.01em}.brand small{display:block;font-size:11px;font-weight:500;color:#475569;letter-spacing:.08em;text-transform:uppercase}
  .meta{text-align:right;font-size:12px;color:#475569}.meta b{display:block;font-size:16px;color:#0f172a;font-family:ui-monospace,Menlo,monospace}
  table{border-collapse:collapse;width:100%;max-width:560px}th,td{text-align:left;padding:7px 10px;border-bottom:1px solid #e2e8f0;vertical-align:top}
  th{width:42%;font-weight:600;color:#475569}td.strong{font-weight:800;font-size:15px}
  .who{display:flex;gap:12px;margin:0 0 14px}.who div{flex:1;background:#f1f5f9;border-radius:10px;padding:10px 14px}
  .who span{display:block;font-size:11px;color:#475569;text-transform:uppercase;letter-spacing:.08em}.who b{display:block;font-size:24px;line-height:1.15;font-weight:800}.who i{display:block;font-style:normal;font-size:12px;color:#475569;margin-top:2px}
  .arch{display:flex;gap:12px;margin:14px 0 18px}.arch div{flex:1;border:1.5px solid #0f172a;border-radius:10px;padding:10px 12px}
  .arch b{display:block;font-size:26px;line-height:1.1}.arch span{font-size:11px;color:#475569;text-transform:uppercase;letter-spacing:.06em}
  .note{margin-top:14px;padding:10px 12px;background:#f1f5f9;border-radius:8px;white-space:pre-wrap}
  .foot{margin-top:26px;font-size:11px;color:#64748b;display:flex;justify-content:space-between}
  .sig{margin-top:34px;display:flex;gap:40px}.sig div{flex:1;border-top:1px solid #0f172a;padding-top:6px;font-size:11px;color:#475569}
  @media print{body{padding:12px}}
</style></head><body>
<div class="head"><div class="brand">Alignova<small>Clear aligner laboratory · case proforma</small></div>
<div class="meta">Case<b>${esc(p.caseNumber)}</b>${esc(formatDate(p.preparedAt))}</div></div>
<div class="who">
  <div><span>Patient</span><b>${esc(p.patientName)}</b>${p.patientCode ? `<i>${esc(p.patientCode)}</i>` : ''}</div>
  <div><span>Clinic</span><b>${esc(p.clinicName ?? p.doctorName ?? '-')}</b>${p.clinicName && p.doctorName ? `<i>${esc(p.doctorName)}</i>` : ''}</div>
</div>
<div class="arch">
  <div><span>Upper aligners</span><b>${esc(p.upperTrays ?? (p.arch === 'lower' ? 0 : '-'))}</b></div>
  <div><span>Lower aligners</span><b>${esc(p.lowerTrays ?? (p.arch === 'upper' ? 0 : '-'))}</b></div>
  <div><span>Total</span><b>${esc(p.trays)}</b></div>
</div>
<table>
${row('Doctor', p.doctorName)}
${row('Group ID', p.groupId)}
${row('Case type', p.caseTypes.join(', '))}
${row('Models', p.archLabel)}
${row('Turnaround', `${WF_PRIORITY_LABELS[p.priority] ?? p.priority}${p.dueDate ? ` · due ${formatDate(p.dueDate)}` : ''}`)}
${p.setupVersion > 0 ? row('Setup version', `V${p.setupVersion}`) : ''}
${price}
</table>
${p.patientInstructions ? `<div class="note"><b>Doctor's instructions</b><br>${esc(p.patientInstructions)}</div>` : ''}
<div class="sig"><div>Packed by</div><div>Checked by</div><div>Received by (clinic)</div></div>
<div class="foot"><span>Please check the upper / lower counts against the box on receipt.</span><span>alignova.cloud</span></div>
<script>window.addEventListener('load',function(){setTimeout(function(){window.print()},300)})</script>
</body></html>`;
}
